import _ from 'lodash';
import PropTypes from 'prop-types';
import React, {useEffect, useState} from 'react';
import PasswordShowHide from './PasswordShowHide';
import SingleInputFormUser from './SingleInputFormUser';
import {FormControl, AlertType, AlertUsername, AlertPassword, AlertEmail} from '../../constants/editUserEnum';
import {GroupName, GroupNameRole, ModalAccountRight} from '../../constants/userManagementEnum';
import withIntl from '../../lib/withIntl';

function FormUserContainer({
  intl,
  inputUsername,
  inputPassword,
  inputConfirmPassword,
  inputName,
  inputEmail,
  initialUserRole,
  roleId,
  isActive,
  alertUsername,
  alertPassword,
  alertConfirmPassword,
  alertEmail,
  isEditUser,
  isAccount,
  availableRoleRights,
  availableRoles,
  onChangeInputUsername,
  onChangeInputPassword,
  onChangeInputConfirmPassword,
  onChangeInputName,
  onBlurInputName,
  onInputKeyPressed,
  onChangeInputEmail,
  onSelectRole,
  onCheckUserRoleRights,
  checkedChangedActive,
}) {
  const roleLevel = roleId - 1;
  const [selectedUserRoleRights, setSelectedUserRoleRights] = useState(null);
  const groupName = `${isAccount ? GroupName.MODAL_ACCOUNT : GroupName.MODAL_EDIT_ACCOUNT}.${GroupNameRole[initialUserRole ? initialUserRole - 1 : roleLevel]}`;

  const showAlertDialog = (type) => {
    let content = '';

    switch (type) {
      case AlertType.USERNAME:
        if (alertUsername.isShow) {
          switch (alertUsername.type) {
            case AlertUsername.USERNAME_EXIST:
              content = intl.formatMessage({id: 'userManagement.createNewUser.usernameExist'});
              break;
            case AlertUsername.SAME_WITH_PASSWORD:
              content = intl.formatMessage({id: 'userManagement.createNewUser.sameWithUsername'});
              break;
            default:
              break;
          }
        }
        break;
      case AlertType.PASSWORD:
        if (alertPassword.isShow) {
          switch (alertPassword.type) {
            case AlertPassword.SAME_WITH_USERNAME:
              content = intl.formatMessage({id: 'userManagement.createNewUser.sameWithUsername'});
              break;
            case AlertPassword.TOO_SHORT:
              content = intl.formatMessage({id: 'userManagement.createNewUser.tooshortPassword'});
              break;
            case AlertPassword.MUST_CONTAIN_FORMAT:
              content = intl.formatMessage({id: 'userManagement.createNewUser.mustContainFormat'});
              break;
            default:
              break;
          }
        }
        break;
      case AlertType.CONFIRM_PASSWORD:
        if (alertConfirmPassword.isShow) {
          switch (alertConfirmPassword.type) {
            case AlertPassword.CONFIRM_PASSWORD_NOT_MATCH:
              content = intl.formatMessage({id: 'userManagement.changePassword.confirmPasswordNotSame'});
              break;
            default:
              break;
          }
        }
        break;
      case AlertType.EMAIL:
        if (alertEmail.isShow) {
          switch (alertEmail.type) {
            case AlertEmail.EMAIL_NOT_VALID:
              content = intl.formatMessage({id: 'userManagement.createNewUser.emailNotValid'});
              break;
            case AlertEmail.EMAIL_ALREADY_EXIST:
              content = intl.formatMessage({id: 'userManagement.createNewUser.emailAlreadyExist'});
              break;
            default:
              break;
          }
        }
        break;
      default:
        break;
    }

    return content;
  };

  const disabledFieldByRole = (field) => {
    const getRoleUser = _.find(selectedUserRoleRights, {groupName, componentName: field});

    if (getRoleUser) return true;

    return false;
  };

  const disabledField = (field) => {
    if (!selectedUserRoleRights) return true;
    if (!isEditUser && !isAccount) return false;

    switch (field) {
      case FormControl.USERNAME.FIELD_KEY:
        return disabledFieldByRole(ModalAccountRight.DISABLED_USERNAME);
      case FormControl.PASSWORD.FIELD_KEY:
        return disabledFieldByRole(ModalAccountRight.DISABLED_PASSWORD);
      case FormControl.CONFIRM_PASSWORD.FIELD_KEY:
        return disabledFieldByRole(ModalAccountRight.DISABLED_CONFIRM_PASSWORD);
      case FormControl.NAME.FIELD_KEY:
        return disabledFieldByRole(ModalAccountRight.DISABLED_NAME);
      case FormControl.EMAIL.FIELD_KEY:
        return disabledFieldByRole(ModalAccountRight.DISABLED_EMAIL);
      case FormControl.ACCESS_ROLE.FIELD_KEY:
        return disabledFieldByRole(ModalAccountRight.DISABLED_ROLE);
      case FormControl.ACTIVE.FIELD_KEY:
        return disabledFieldByRole(ModalAccountRight.DISABLED_ACTIVITY);
      default:
        return false;
    }
  };

  const alertMessageUsername = showAlertDialog(AlertType.USERNAME);

  const alertMessagePassword = showAlertDialog(AlertType.PASSWORD);

  const alertMessageConfirmPassword = showAlertDialog(AlertType.CONFIRM_PASSWORD);

  const alertMessageEmail = showAlertDialog(AlertType.EMAIL);

  useEffect(() => {
    const getSelectedUserRoleRights = async () => {
      const userRoleRights = isAccount ? availableRoleRights : await onCheckUserRoleRights(roleId, false);
      setSelectedUserRoleRights(userRoleRights);
    };

    getSelectedUserRoleRights();

    const onHandleKeydown = (event) => {
      if (event.key === 'Enter') {
        onInputKeyPressed();
      }
    };

    document.addEventListener('keydown', onHandleKeydown);

    return () => document.removeEventListener('keydown', onHandleKeydown);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      <SingleInputFormUser
        labelHeader={FormControl.USERNAME.LABEL_HEADER}
        type={FormControl.USERNAME.TYPE}
        inputType={FormControl.USERNAME.INPUT_TYPE}
        placeholder={FormControl.USERNAME.PLACEHOLDER}
        onChange={onChangeInputUsername}
        glyph={FormControl.USERNAME.GLYPH}
        value={inputUsername}
        disabled={disabledField(FormControl.USERNAME.FIELD_KEY)}
        alertMessageContent={alertUsername.isShow ? alertMessageUsername : ''}
        required
        id="new-user-username"
      />
      {!isAccount && (
        <PasswordShowHide
          labelHeader={FormControl.PASSWORD.LABEL_HEADER}
          placeholder={FormControl.PASSWORD.PLACEHOLDER}
          onChange={onChangeInputPassword}
          value={inputPassword}
          disabled={disabledField(FormControl.PASSWORD.FIELD_KEY)}
          alertMessageContent={alertPassword.isShow ? alertMessagePassword : ''}
          required
          id="new-user-password"
        />
      )}
      {!isAccount && (
        <PasswordShowHide
          labelHeader={FormControl.CONFIRM_PASSWORD.LABEL_HEADER}
          placeholder={FormControl.CONFIRM_PASSWORD.PLACEHOLDER}
          onChange={onChangeInputConfirmPassword}
          value={inputConfirmPassword}
          disabled={disabledField(FormControl.CONFIRM_PASSWORD.FIELD_KEY)}
          alertMessageContent={alertConfirmPassword.isShow ? alertMessageConfirmPassword : ''}
          required
          id="new-user-confirm-password"
        />
      )}
      <SingleInputFormUser
        labelHeader={FormControl.NAME.LABEL_HEADER}
        type={FormControl.NAME.TYPE}
        inputType={FormControl.NAME.INPUT_TYPE}
        placeholder={FormControl.NAME.PLACEHOLDER}
        onChange={onChangeInputName}
        onBlur={onBlurInputName}
        value={inputName}
        disabled={disabledField(FormControl.NAME.FIELD_KEY)}
        required
        id="new-user-name"
      />
      <SingleInputFormUser
        labelHeader={FormControl.EMAIL.LABEL_HEADER}
        type={FormControl.EMAIL.TYPE}
        inputType={FormControl.EMAIL.INPUT_TYPE}
        placeholder={FormControl.EMAIL.PLACEHOLDER}
        onChange={onChangeInputEmail}
        glyph={FormControl.EMAIL.GLYPH}
        value={inputEmail}
        disabled={disabledField(FormControl.EMAIL.FIELD_KEY)}
        alertMessageContent={alertEmail.isShow ? alertMessageEmail : ''}
        required
        id="new-user-email"
      />
      <SingleInputFormUser
        labelHeader={FormControl.ACCESS_ROLE.LABEL_HEADER}
        type={FormControl.ACCESS_ROLE.TYPE}
        valueKey={FormControl.ACCESS_ROLE.VALUE_KEY}
        labelKey={FormControl.ACCESS_ROLE.LABEL_KEY}
        placeholder={FormControl.ACCESS_ROLE.PLACEHOLDER}
        options={availableRoles}
        onSelect={onSelectRole}
        value={roleId}
        disabled={disabledField(FormControl.ACCESS_ROLE.FIELD_KEY)}
        required
        id="new-user-role"
      />
      {isEditUser && (
        <SingleInputFormUser
          type={FormControl.ACTIVE.TYPE}
          label={isActive ? FormControl.ACTIVE.LABEL : FormControl.ACTIVE.LABEL_INACTIVE}
          checked={isActive}
          checkedChanged={checkedChangedActive}
          alertMessageContent={!isActive && FormControl.ACTIVE.HINT_INACTIVE}
          disabled={disabledField(FormControl.ACTIVE.FIELD_KEY)}
          id="new-user-active-inactive"
        />
      )}
    </>
  );
}

FormUserContainer.propTypes = {
  intl: PropTypes.any.isRequired,
  inputUsername: PropTypes.string,
  inputPassword: PropTypes.string,
  inputConfirmPassword: PropTypes.string,
  inputName: PropTypes.string,
  inputEmail: PropTypes.string,
  initialUserRole: PropTypes.string,
  roleId: PropTypes.string,
  isActive: PropTypes.bool,
  alertUsername: PropTypes.object,
  alertPassword: PropTypes.object,
  alertConfirmPassword: PropTypes.object,
  alertEmail: PropTypes.object,
  isEditUser: PropTypes.bool,
  isAccount: PropTypes.bool,
  availableRoleRights: PropTypes.array,
  availableRoles: PropTypes.array,
  onChangeInputUsername: PropTypes.func,
  onChangeInputPassword: PropTypes.func,
  onChangeInputConfirmPassword: PropTypes.func,
  onChangeInputName: PropTypes.func,
  onBlurInputName: PropTypes.func,
  onChangeInputEmail: PropTypes.func,
  onSelectRole: PropTypes.func,
  onCheckUserRoleRights: PropTypes.func,
  checkedChangedActive: PropTypes.func,
  onInputKeyPressed: PropTypes.func,
};

FormUserContainer.defaultProps = {
  inputUsername: '',
  inputPassword: '',
  inputConfirmPassword: '',
  inputName: '',
  inputEmail: '',
  initialUserRole: '-1',
  roleId: '-1',
  isActive: false,
  alertUsername: {},
  alertPassword: {},
  alertConfirmPassword: {},
  alertEmail: {},
  isEditUser: false,
  isAccount: false,
  availableRoleRights: [],
  availableRoles: [],
  onChangeInputUsername: () => {},
  onChangeInputPassword: () => {},
  onChangeInputConfirmPassword: () => {},
  onChangeInputName: () => {},
  onBlurInputName: () => {},
  onChangeInputEmail: () => {},
  onSelectRole: () => {},
  onCheckUserRoleRights: () => {},
  checkedChangedActive: () => {},
  onInputKeyPressed: () => {},
};

export default withIntl(FormUserContainer);
